import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Crown, Database, FileCheck2, HardDrive, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { PolicyUploadPanel } from '../components/isms/PolicyUploadPanel';
import { PolicyLibrary } from '../components/isms/PolicyLibrary';
import API_URL from '../lib/apiUrl';

const ISMSDocumentControl = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [policyData, setPolicyData] = useState({ items: [], total: 0, total_bytes: 0, page: 1, pages: 1 });
  const [pageLoading, setPageLoading] = useState(true);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const loadPolicies = useCallback(async () => {
    setLibraryLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/isms-documents`, { headers: { Authorization: `Bearer ${token}` }, params: { page, page_size: 20, search: appliedSearch } });
      setPolicyData(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load the policy register.');
    } finally {
      setLibraryLoading(false);
    }
  }, [token, page, appliedSearch]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/subscription/status`, { headers });
        setSubscription(data);
        if (data.plan === 'pro' && data.status === 'active') await loadPolicies();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Could not verify Pro access.');
      } finally {
        setPageLoading(false);
      }
    };
    if (token) initialize();
  }, [token, loadPolicies, headers]);

  const handleUpload = async ({ policyName, policyOwner, policyVersion, file }) => {
    const formData = new FormData();
    formData.append('policy_name', policyName);
    formData.append('policy_owner', policyOwner);
    formData.append('policy_version', policyVersion);
    formData.append('attachment', file);
    setUploading(true);
    setUploadProgress(0);
    try {
      await axios.post(`${API_URL}/api/isms-documents`, formData, { headers, onUploadProgress: (event) => setUploadProgress(event.total ? Math.round((event.loaded * 100) / event.total) : 0) });
      toast.success('Policy document saved securely.');
      setPage(1);
      if (page === 1) await loadPolicies();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Policy upload failed.');
      return false;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (policy) => {
    setDownloadingId(policy.policy_id);
    try {
      const response = await axios.get(`${API_URL}/api/isms-documents/${policy.policy_id}/download`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = policy.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Policy download started.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Policy download failed.');
    } finally {
      setDownloadingId('');
    }
  };

  const handleDelete = async (policyId) => {
    setDeletingId(policyId);
    try {
      await axios.delete(`${API_URL}/api/isms-documents/${policyId}`, { headers });
      toast.success('Policy document deleted.');
      await loadPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Policy deletion failed.');
    } finally {
      setDeletingId('');
    }
  };

  if (pageLoading) return <div className="dashboard-shell flex min-h-screen items-center justify-center text-sm text-slate-400" data-testid="isms-page-loading">Preparing document control...</div>;

  if (subscription?.plan !== 'pro' || subscription?.status !== 'active') {
    return <div className="dashboard-shell flex min-h-screen items-center justify-center px-4"><div className="dashboard-glass w-full max-w-lg rounded-xl p-8 text-center" data-testid="isms-pro-access-required"><Crown className="mx-auto h-10 w-10 text-amber-300" /><h1 className="mt-5 font-heading text-3xl font-semibold text-white">Pro Edition required</h1><p className="mt-3 text-sm leading-6 text-slate-400">ISMS Document Control is reserved for active Pro Edition workspaces.</p><Button onClick={() => navigate('/dashboard')} className="mt-6 bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="isms-access-back-button"><ArrowLeft className="mr-2 h-4 w-4" />Back to dashboard</Button></div></div>;
  }

  return (
    <div className="dashboard-shell min-h-screen overflow-x-hidden pb-10 text-slate-100" data-testid="isms-document-control-page">
      <nav className="sticky top-0 z-40 border-b border-white/10 bg-slate-950/75 backdrop-blur-2xl" data-testid="isms-navigation"><div className="mx-auto flex min-h-16 w-full max-w-screen-2xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8"><button type="button" onClick={() => navigate('/dashboard')} className="flex items-center gap-3 text-left" data-testid="isms-back-to-dashboard"><div className="flex h-10 w-10 items-center justify-center rounded-lg border border-emerald-400/30 bg-emerald-400/10"><ShieldCheck className="h-5 w-5 text-emerald-300" /></div><div><p className="font-heading text-base font-semibold text-white sm:text-lg">AIVORA Comply360</p><p className="hidden text-[11px] text-slate-500 sm:block">ISMS Document Control</p></div></button><span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-xs font-semibold text-amber-200" data-testid="isms-pro-badge"><Crown className="h-3.5 w-3.5" />Pro Edition</span></div></nav>

      <main className="mx-auto w-full max-w-screen-2xl px-4 py-8 sm:px-6 lg:px-8">
        <header className="mb-8 flex flex-col justify-between gap-5 lg:flex-row lg:items-end" data-testid="isms-page-header"><div><button type="button" onClick={() => navigate('/dashboard')} className="mb-5 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white" data-testid="isms-header-back-button"><ArrowLeft className="h-4 w-4" />Dashboard</button><p className="dashboard-eyebrow">Pro workspace</p><h1 className="mt-3 font-heading text-4xl font-semibold text-white sm:text-5xl" data-testid="isms-page-title">ISMS Document Control</h1><p className="mt-3 max-w-2xl text-sm leading-6 text-slate-400 sm:text-base" data-testid="isms-page-description">Maintain a secure, searchable register of policies, owners, versions, and controlled attachments.</p></div><div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3" data-testid="isms-user-identity"><p className="text-[10px] uppercase text-slate-500">Signed in as</p><p className="mt-1 max-w-xs truncate text-sm text-slate-200">{user?.email}</p></div></header>

        <section className="mb-6 grid gap-4 sm:grid-cols-3" data-testid="isms-storage-metrics"><div className="dashboard-glass rounded-xl p-5"><FileCheck2 className="h-5 w-5 text-emerald-300" /><p className="mt-5 font-heading text-3xl font-semibold text-white" data-testid="isms-policy-count">{policyData.total}</p><p className="mt-1 text-sm text-slate-400">Controlled policies</p></div><div className="dashboard-glass rounded-xl p-5"><HardDrive className="h-5 w-5 text-sky-300" /><p className="mt-5 font-heading text-3xl font-semibold text-white" data-testid="isms-storage-used">{policyData.total_bytes < 1024 * 1024 ? `${policyData.total_bytes ? Math.max(1, Math.round(policyData.total_bytes / 1024)) : 0} KB` : `${(policyData.total_bytes / (1024 * 1024)).toFixed(1)} MB`}</p><p className="mt-1 text-sm text-slate-400">Storage used</p></div><div className="dashboard-glass rounded-xl p-5"><Database className="h-5 w-5 text-amber-300" /><p className="mt-5 font-heading text-3xl font-semibold text-white">15 MB</p><p className="mt-1 text-sm text-slate-400">Maximum per file</p></div></section>

        <div className="grid gap-6 xl:grid-cols-12"><div className="xl:col-span-4"><PolicyUploadPanel uploading={uploading} progress={uploadProgress} onUpload={handleUpload} /></div><div className="min-w-0 xl:col-span-8"><PolicyLibrary data={policyData} loading={libraryLoading} search={search} onSearchChange={setSearch} onSearch={() => { setPage(1); setAppliedSearch(search.trim()); }} onPageChange={setPage} onDownload={handleDownload} onDelete={handleDelete} downloadingId={downloadingId} deletingId={deletingId} /></div></div>
      </main>
    </div>
  );
};

export default ISMSDocumentControl;